/**
 * Products Redux Sagas
 */

import { call, put, takeEvery, takeLatest, all, fork } from '@spcc/core'
import { productsActions } from './products.slice'
import { productsApi, skuApi, variantApi, categoryApi } from '../infrastructure/api'
import type {
  SKU,
  Variant,
  VariantPortalAvailability,
  Category,
  CreateSKURequest,
  UpdateSKURequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../domain/types'

// ============================================================================
// Fetch Products Saga
// ============================================================================

function* fetchProductsSaga() {
  try {
    const result: { skus: SKU[]; variants: Variant[]; categories: Category[] } = yield call(
      productsApi.getProductsData
    )
    yield put(productsActions.fetchProductsSuccess(result))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch products'
    yield put(productsActions.fetchProductsFailure(message))
  }
}

// ============================================================================
// SKU Sagas
// ============================================================================

function* createSkuSaga(action: ReturnType<typeof productsActions.createSku>) {
  try {
    const request: CreateSKURequest = action.payload
    const createdSku: SKU = yield call(skuApi.create, request)
    const hydratedSku: SKU | null = yield call(skuApi.getById, createdSku.documentId)
    yield put(productsActions.createSkuSuccess(hydratedSku ?? createdSku))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create SKU'
    yield put(productsActions.createSkuFailure(message))
  }
}

function* updateSkuSaga(action: ReturnType<typeof productsActions.updateSku>) {
  try {
    const request: UpdateSKURequest = action.payload
    const updatedSku: SKU = yield call(skuApi.update, request)
    const hydratedSku: SKU | null = yield call(skuApi.getById, updatedSku.documentId)
    yield put(productsActions.updateSkuSuccess(hydratedSku ?? updatedSku))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update SKU'
    yield put(productsActions.updateSkuFailure(message))
  }
}

function* deleteSkuSaga(action: ReturnType<typeof productsActions.deleteSku>) {
  try {
    const documentId: string = action.payload
    const success: boolean = yield call(skuApi.delete, documentId)
    if (success) {
      yield put(productsActions.deleteSkuSuccess(documentId))
    } else {
      yield put(productsActions.deleteSkuFailure('Failed to delete SKU'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete SKU'
    yield put(productsActions.deleteSkuFailure(message))
  }
}

// ============================================================================
// Variant Sagas
// ============================================================================

function* createVariantSaga(action: ReturnType<typeof productsActions.createVariant>) {
  try {
    const request: CreateVariantRequest = action.payload
    const createdVariant: Variant = yield call(variantApi.create, request)
    const hydratedVariant: Variant | null = yield call(variantApi.getById, createdVariant.documentId)
    yield put(productsActions.createVariantSuccess(hydratedVariant ?? createdVariant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create variant'
    yield put(productsActions.createVariantFailure(message))
  }
}

function* updateVariantSaga(action: ReturnType<typeof productsActions.updateVariant>) {
  try {
    const request: UpdateVariantRequest = action.payload
    const updatedVariant: Variant = yield call(variantApi.update, request)
    const hydratedVariant: Variant | null = yield call(variantApi.getById, updatedVariant.documentId)
    yield put(productsActions.updateVariantSuccess(hydratedVariant ?? updatedVariant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update variant'
    yield put(productsActions.updateVariantFailure(message))
  }
}

function* deleteVariantSaga(action: ReturnType<typeof productsActions.deleteVariant>) {
  try {
    const documentId: string = action.payload
    const success: boolean = yield call(variantApi.delete, documentId)
    if (success) {
      yield put(productsActions.deleteVariantSuccess(documentId))
    } else {
      yield put(productsActions.deleteVariantFailure('Failed to delete variant'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete variant'
    yield put(productsActions.deleteVariantFailure(message))
  }
}

function* updateVariantPortalAvailabilitySaga(
  action: ReturnType<typeof productsActions.updateVariantPortalAvailability>
) {
  try {
    const { documentId, portalAvailability }: {
      documentId: string
      portalAvailability: VariantPortalAvailability
    } = action.payload

    const updatedPortalAvailability: VariantPortalAvailability = yield call(
      variantApi.updatePortalAvailability,
      documentId,
      portalAvailability
    )

    yield put(
      productsActions.updateVariantPortalAvailabilitySuccess({
        documentId,
        portalAvailability: updatedPortalAvailability,
      })
    )
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update portal availability'
    yield put(
      productsActions.updateVariantPortalAvailabilityFailure({
        documentId: action.payload.documentId,
        message,
      })
    )
  }
}

// ============================================================================
// Category Sagas
// ============================================================================

function* createCategorySaga(action: ReturnType<typeof productsActions.createCategory>) {
  try {
    const request: CreateCategoryRequest = action.payload
    const created: Category = yield call(categoryApi.create, request)
    const hydrated: Category | null = yield call(categoryApi.getById, created.documentId)
    yield put(productsActions.createCategorySuccess(hydrated ?? created))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create category'
    yield put(productsActions.createCategoryFailure(message))
  }
}

function* updateCategorySaga(action: ReturnType<typeof productsActions.updateCategory>) {
  try {
    const request: UpdateCategoryRequest = action.payload
    const updated: Category = yield call(categoryApi.update, request)
    const hydrated: Category | null = yield call(categoryApi.getById, updated.documentId)
    yield put(productsActions.updateCategorySuccess(hydrated ?? updated))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update category'
    yield put(productsActions.updateCategoryFailure(message))
  }
}

// ============================================================================
// Fetch-for-Edit Sagas (bilingual: EN + AR fields populated)
// ============================================================================

function* fetchSkuForEditSaga(action: ReturnType<typeof productsActions.fetchSkuForEdit>) {
  try {
    const sku: SKU | null = yield call(skuApi.getById, action.payload)
    if (sku) {
      yield put(productsActions.fetchSkuForEditSuccess(sku))
    } else {
      yield put(productsActions.fetchSkuForEditFailure('SKU not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load SKU for editing'
    yield put(productsActions.fetchSkuForEditFailure(message))
  }
}

function* fetchVariantForEditSaga(
  action: ReturnType<typeof productsActions.fetchVariantForEdit>
) {
  try {
    const variant: Variant | null = yield call(variantApi.getById, action.payload)
    if (variant) {
      yield put(productsActions.fetchVariantForEditSuccess(variant))
    } else {
      yield put(productsActions.fetchVariantForEditFailure('Variant not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load variant for editing'
    yield put(productsActions.fetchVariantForEditFailure(message))
  }
}

function* fetchCategoryForEditSaga(
  action: ReturnType<typeof productsActions.fetchCategoryForEdit>
) {
  try {
    const category: Category | null = yield call(categoryApi.getById, action.payload)
    if (category) {
      yield put(productsActions.fetchCategoryForEditSuccess(category))
    } else {
      yield put(productsActions.fetchCategoryForEditFailure('Category not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load category for editing'
    yield put(productsActions.fetchCategoryForEditFailure(message))
  }
}

// ============================================================================
// Watcher
// ============================================================================

function* watchProducts() {
  yield takeLatest(productsActions.fetchProducts.type, fetchProductsSaga)
  yield takeLatest(productsActions.createSku.type, createSkuSaga)
  yield takeLatest(productsActions.updateSku.type, updateSkuSaga)
  yield takeLatest(productsActions.deleteSku.type, deleteSkuSaga)
  yield takeLatest(productsActions.createVariant.type, createVariantSaga)
  yield takeLatest(productsActions.updateVariant.type, updateVariantSaga)
  yield takeLatest(productsActions.deleteVariant.type, deleteVariantSaga)
  yield takeLatest(productsActions.createCategory.type, createCategorySaga)
  yield takeLatest(productsActions.updateCategory.type, updateCategorySaga)
  yield takeEvery(
    productsActions.updateVariantPortalAvailability.type,
    updateVariantPortalAvailabilitySaga
  )
  yield takeLatest(productsActions.fetchSkuForEdit.type, fetchSkuForEditSaga)
  yield takeLatest(productsActions.fetchVariantForEdit.type, fetchVariantForEditSaga)
  yield takeLatest(productsActions.fetchCategoryForEdit.type, fetchCategoryForEditSaga)
}

// ============================================================================
// Root Saga
// ============================================================================

export function* productsSaga() {
  yield all([fork(watchProducts)])
}
